import type { PlatformSubpageConfig } from '../types';
import { defaultCtas, localImage } from './shared';

const img = (src: string, alt: string) => ({
  src: localImage(src),
  alt,
  width: 1200,
  height: 800,
});

export const securityConfig: PlatformSubpageConfig = {
  slug: 'security',
  path: '/platform/security',
  breadcrumbLabel: 'Security',
  hero: {
    title: 'Enterprise security built for connected fleets',
    subtitle:
      'End-to-end encryption, role-based access, secure device auth, and compliance-ready infrastructure.',
    ...defaultCtas,
  },
  heroImage: {
    src: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&q=80',
    alt: 'Secure cloud infrastructure',
    width: 800,
    height: 520,
  },
  eduIntro: {
    title: 'What is IoT platform security?',
    description:
      'IoT security protects devices, data in transit, and tenant boundaries through encryption, authentication, access controls, and audit logging—so fleets stay trustworthy at scale.',
  },
  advantages: {
    eyebrow: 'COMPETITIVE EDGE',
    title: 'Security built for scale and compliance',
    items: [
      { icon: 'lock', title: 'End-to-End Encryption', description: 'TLS in transit and encrypted storage for credentials and tenant data.' },
      { icon: 'key', title: 'Role-Based Access', description: 'Granular roles for partners, support, and operations teams.' },
      { icon: 'shield', title: 'Device Trust', description: 'Secure provisioning and authentication for every connected endpoint.' },
    ],
  },
  featureShowcase: {
    eyebrow: 'SECURITY LAYERS',
    title: 'Defense in depth across the platform',
    subtitle: 'Explore compliance capabilities and platform reliability metrics.',
    items: [
      {
        title: 'Encryption everywhere',
        description: 'TLS in transit and encrypted storage for device credentials and tenant data.',
        image: img('/images/home/feature-cloud.jpg', 'Encryption'),
        cta: { label: 'Learn encryption', to: '/contact-us' },
      },
      {
        title: 'Role-based access control',
        description: 'Least-privilege access for every team member across partners and operations.',
        image: img('/images/home/partner-dashboard.png', 'RBAC'),
        cta: { label: 'View access controls', to: '/contact-us' },
      },
      {
        title: 'Audit and compliance',
        description: 'Immutable logs, GDPR-ready workflows, and processes aligned with enterprise expectations.',
        image: img('/images/home/feature-firmware.jpg', 'Compliance'),
        cta: { label: 'Explore compliance', to: '/contact-us' },
      },
    ],
  },
  capabilities: {
    eyebrow: 'CAPABILITIES',
    title: 'Enterprise-grade security controls',
    items: [
      { icon: 'lock', title: 'End-to-End Encryption', description: 'Protect data across devices, cloud, and apps.' },
      { icon: 'key', title: 'Role-Based Access Control', description: 'Least-privilege access for every team member.' },
      { icon: 'shield', title: 'Secure Device Authentication', description: 'Mutual TLS and rotating credentials.' },
      { icon: 'globe', title: 'GDPR Ready Infrastructure', description: 'Export, deletion, and residency options.' },
      { icon: 'server', title: 'Audit Logs', description: 'Track admin actions and API activity.' },
      { icon: 'activity', title: 'ISO Security Standards', description: 'Processes aligned with enterprise expectations.' },
    ],
  },
  ctaBanner: {
    title: 'Secure your connected fleet with enterprise-grade controls',
    ctaLabel: 'Get Started',
    ctaTo: '/contact-us',
    imageSrc: '/images/cta/solution-cta-robot.webp',
  },
  faqs: {
    title: 'Frequently asked questions',
    items: [
      { question: 'Is data encrypted in transit?', answer: 'Yes—TLS encryption for all device, API, and app communication paths.' },
      { question: 'Do you support GDPR?', answer: 'Export, deletion, and data residency workflows for EU customers.' },
      { question: 'Are audit logs available?', answer: 'Immutable logs for admin actions, API calls, and device state changes.' },
    ],
  },
  sections: {
    eyebrow: 'ARCHITECTURE',
    title: 'Defense in depth across the platform',
    subtitle: 'Explore compliance capabilities and platform reliability metrics.',
  },
};

export const securityStats = [
  { value: 99.99, suffix: '%', label: 'Cloud Uptime', decimals: 2 },
  { value: 10, suffix: 'M+', label: 'Device Capacity', decimals: 0 },
  { value: 256, suffix: '-bit', label: 'Encryption', decimals: 0 },
  { value: 24, suffix: '/7', label: 'Audit Logging', decimals: 0 },
] as const;

export const securityCompliance = [
  {
    id: 'encryption',
    title: 'End-to-End Encryption',
    body: 'TLS in transit and encrypted storage for device credentials and tenant data.',
  },
  {
    id: 'rbac',
    title: 'Role-Based Access Control',
    body: 'Granular roles for partners, support, and operations teams.',
  },
  {
    id: 'gdpr',
    title: 'GDPR Ready',
    body: 'Data residency options, export, and deletion workflows for EU customers.',
  },
  {
    id: 'audit',
    title: 'Audit Logs',
    body: 'Immutable logs for admin actions, API calls, and device state changes.',
  },
] as const;
